import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Alert,
  Button,
  Switch,
  FormControlLabel,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import DomainIcon from "@mui/icons-material/Domain";
import UploadIcon from "@mui/icons-material/Upload";
import RefreshIcon from "@mui/icons-material/Refresh";

import {
  fetchAvailableDates,
  fetchVarPercentiles,
  fetchVarByStrategy,
  fetchFirmwideDashboard,
  fetchStrategyDashboard,
  fetchConvertTakeoutRisk,
  refreshCache,
} from "../../../services/RiskMonitorAxios";
import { useSearchParams } from "react-router-dom";
import { STRATEGY_DISPLAY_ORDER, titleCaseStrategyName } from "./strategyConfig";
import PnlDistributionChart from "./PnlDistributionChart";
import HistoricalVarChart from "./HistoricalVarChart";
import StrategyBreakdownModal from "./StrategyBreakdownModal";
import StressScenarioPanel from "./StressScenarioPanel";
import ConvertTakeoutRiskPanel from "./ConvertTakeoutRiskPanel";
import RiskMonitorUpload from "./RiskMonitorUpload";
import { formatFactorValue } from "./FactorPanel";
import { useAuth } from "../../../context/AuthContext";
import { fetchNavByDate } from "../../../services/Axios";
import { fetchRiskLimitsExposure } from "../../../services/Data360Axios";
import GVarLogo from "../../ui/GVarLogo";
import GVarLoader from "../../ui/GVarLoader";
import { withCommas } from "./numberFormat";

function formatCurrency(value, { asBps = false, navNumber } = {}) {
  if (value == null || isNaN(value)) return "—";
  if (asBps && navNumber) {
    return `${((value / navNumber) * 10000).toFixed(1)} bps`;
  }
  const abs = Math.abs(value);
  if (abs >= 1e6) return `$${withCommas(value / 1e6)}M`;
  if (abs >= 1e3) return `$${withCommas(value / 1e3)}K`;
  return `$${value.toFixed(0)}`;
}

// Day-on-day change for a VaR/ES figure. In $ mode this is the relative %
// change vs prev; in bps-of-NAV mode it's the raw difference in NAV basis
// points (today bps - prev bps), since a relative change-of-a-ratio reads as noise.
function formatDeltaValue(today, prev, { asBps = false, navNumber } = {}) {
  const delta = today - prev;
  const isPositive = delta >= 0;
  if (asBps && navNumber) {
    const navBpsDelta = (today / navNumber - prev / navNumber) * 10000;
    return { isPositive, text: `${isPositive ? "+" : ""}${navBpsDelta.toFixed(1)} bps` };
  }
  const pct = prev !== 0 ? (delta / Math.abs(prev)) * 100 : 0;
  return { isPositive, text: `${isPositive ? "+" : ""}${pct.toFixed(1)}%` };
}

function formatFullNumber(value) {
  if (value == null || isNaN(value)) return "N/A";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function ChangeIndicator({ today, prev, asBps, navNumber }) {
  const hasChange =
    today != null && prev != null && !isNaN(today) && !isNaN(prev);
  if (!hasChange)
    return (
      <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.8rem" }}>
        —
      </Typography>
    );

  const delta = today - prev;
  const { isPositive, text } = formatDeltaValue(today, prev, { asBps, navNumber });

  return (
    <Tooltip
      title={`Change: ${formatFullNumber(delta)} (${text})`}
      arrow
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, cursor: "help" }}>
        {isPositive ? (
          <TrendingUpIcon sx={{ fontSize: 16, color: "#006842" }} />
        ) : (
          <TrendingDownIcon sx={{ fontSize: 16, color: "#c53030" }} />
        )}
        <Typography
          variant="caption"
          fontWeight={600}
          sx={{ fontSize: "0.8rem", color: isPositive ? "#006842" : "#c53030" }}
        >
          {text}
        </Typography>
      </Box>
    </Tooltip>
  );
}

function VarBubble({
  label,
  esLabel,
  todayValue,
  prevValue,
  esValue,
  prevEsValue,
  accentColor,
  loading: bubbleLoading,
  onClick,
  asBps,
  navNumber,
}) {
  const hasChange =
    todayValue != null && prevValue != null && !isNaN(todayValue) && !isNaN(prevValue);
  const { isPositive, text: deltaText } = hasChange
    ? formatDeltaValue(todayValue, prevValue, { asBps, navNumber })
    : { isPositive: true, text: "" };

  return (
    <Tooltip
      title={
        <span style={{ whiteSpace: "pre-line" }}>
          {`${label}  VaR: ${formatFullNumber(todayValue)} (prev ${formatFullNumber(prevValue)})\n${esLabel}  ES:  ${formatFullNumber(esValue)} (prev ${formatFullNumber(prevEsValue)})\n\nClick for strategy breakdown`}
        </span>
      }
      arrow
    >
      <Box
        onClick={onClick}
        sx={{
          width: "100%",
          bgcolor: "background.paper",
          borderRadius: 2,
          p: { xs: 1.25, sm: 2 },
          cursor: "pointer",
          position: "relative",
          overflow: "hidden",
          transition: "transform 0.15s ease, box-shadow 0.15s ease",
          boxShadow: "0 1px 3px rgba(0,0,0,0.07), 0 4px 12px rgba(0,0,0,0.05)",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
          },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0, left: 0, right: 0,
            height: "3px",
            background: accentColor,
            borderRadius: "2px 2px 0 0",
          },
        }}
      >
        {bubbleLoading && (
          <Box sx={{ position: "absolute", inset: 0, bgcolor: "rgba(255,255,255,0.75)", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 2, zIndex: 1 }}>
            <CircularProgress size={22} sx={{ color: accentColor }} />
          </Box>
        )}

        {/* Label */}
        <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.09em", color: accentColor, mb: { xs: 0.75, sm: 1.25 } }}>
          {label} / {esLabel}
        </Typography>

        {/* Metrics row — VaR · slash · ES */}
        <Box sx={{ display: "flex", alignItems: "baseline", gap: { xs: 0.5, sm: 1 }, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: { xs: "1.2rem", sm: "1.55rem" }, fontWeight: 800, fontFamily: "monospace", color: "text.primary", lineHeight: 1 }}>
            {formatCurrency(todayValue, { asBps, navNumber })}
          </Typography>
          <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" }, color: "text.disabled", fontWeight: 400, lineHeight: 1, mx: 0.25 }}>
            /
          </Typography>
          <Typography sx={{ fontSize: { xs: "1.2rem", sm: "1.55rem" }, fontWeight: 700, fontFamily: "monospace", color: accentColor, lineHeight: 1 }}>
            {formatCurrency(esValue, { asBps, navNumber })}
          </Typography>
        </Box>

        {/* Prev row */}
        <Typography sx={{ fontSize: "0.7rem", color: "text.disabled", mt: 0.75, fontFamily: "monospace" }}>
          prev {formatCurrency(prevValue, { asBps, navNumber })} / {formatCurrency(prevEsValue ?? null, { asBps, navNumber })}
        </Typography>

        {/* Delta chip */}
        {hasChange && (
          <Box sx={{ mt: { xs: 0.75, sm: 1.25 } }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.4,
                px: 0.85,
                py: 0.2,
                borderRadius: 1,
                bgcolor: isPositive ? "rgba(0,104,66,0.07)" : "rgba(197,48,48,0.07)",
              }}
            >
              {isPositive
                ? <TrendingUpIcon sx={{ fontSize: 12, color: "#006842" }} />
                : <TrendingDownIcon sx={{ fontSize: 12, color: "#c53030" }} />
              }
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: isPositive ? "#006842" : "#c53030" }}>
                {deltaText} vs prev
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Tooltip>
  );
}

function FactorContributionChart({ title, items, barColor, emptyMessage }) {
  const maxAbs = items.length > 0 ? Math.max(...items.map((i) => Math.abs(i.pct_contribution))) || 1 : 1;

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        borderRadius: 2,
        overflow: "hidden",
        boxShadow: "0 1px 3px rgba(0,0,0,0.07), 0 4px 12px rgba(0,0,0,0.05)",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minWidth: 0,
        // matching top accent stripe
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0, left: 0, right: 0,
          height: "3px",
          background: barColor,
          borderRadius: "2px 2px 0 0",
        },
      }}
    >
      {/* Title */}
      <Box sx={{ px: { xs: 1.25, sm: 2 }, pt: { xs: 1.25, sm: 2 }, pb: 1, display: "flex", alignItems: "center", gap: 0.75 }}>
        <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.09em", color: barColor }}>
          {title}
        </Typography>
      </Box>

      {/* Column headers */}
      {items.length > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 }, px: { xs: 2, sm: 2.75 }, pb: 0.25 }}>
          <Typography sx={{ fontSize: "0.6rem", fontWeight: 600, color: "text.disabled", width: { xs: 90, sm: 120, md: 145 }, flexShrink: 0 }}>
            Factor
          </Typography>
          <Tooltip title="Average factor scenario shock across the 95% VaR tail simulations" arrow>
            <Typography sx={{ fontSize: "0.6rem", fontWeight: 600, color: "text.disabled", width: 72, textAlign: "right", flexShrink: 0, cursor: "help" }}>
              Avg Shock
            </Typography>
          </Tooltip>
          <Box sx={{ flex: 1, minWidth: 20 }} />
          <Typography sx={{ fontSize: "0.6rem", fontWeight: 600, color: "text.disabled", width: 44, textAlign: "right", flexShrink: 0 }}>
            Contrib.
          </Typography>
        </Box>
      )}

      {/* Rows */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", px: { xs: 1.25, sm: 2 }, pb: 1.5, gap: 0.5 }}>
        {items.length === 0 ? (
          <Typography variant="caption" sx={{ color: "text.disabled", fontStyle: "italic" }}>
            {emptyMessage}
          </Typography>
        ) : (
          items.map((item, idx) => {
            const barPct = (Math.abs(item.pct_contribution) / maxAbs) * 100;
            const isZero = item.pct_contribution === 0;
            const hasShock = item.mean_shock != null && !isNaN(item.mean_shock);
            return (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 0.5, sm: 1 },
                  minWidth: 0,
                  flex: 1,
                  borderRadius: 1,
                  px: 0.75,
                  transition: "background 0.12s",
                  "&:hover": { bgcolor: "grey.50" },
                }}
              >
                <Tooltip title={`${item.factor}: ${item.pct_contribution.toFixed(2)}%`} arrow>
                  <Typography
                    sx={{
                      fontSize: { xs: "0.68rem", sm: "0.75rem" },
                      color: isZero ? "text.disabled" : "text.secondary",
                      width: { xs: 90, sm: 120, md: 145 },
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      cursor: "help",
                    }}
                  >
                    {item.factor}
                  </Typography>
                </Tooltip>
                <Tooltip title="Average factor scenario shock across the 95% VaR tail simulations" arrow>
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontFamily: "monospace",
                      color: hasShock ? "text.secondary" : "text.disabled",
                      width: 72,
                      textAlign: "right",
                      flexShrink: 0,
                      cursor: "help",
                    }}
                  >
                    {hasShock
                      ? `${item.mean_shock >= 0 ? "+" : ""}${formatFactorValue(item.factor, item.mean_shock)}`
                      : "—"}
                  </Typography>
                </Tooltip>
                <Box sx={{ flex: 1, position: "relative", height: 7, bgcolor: "grey.100", borderRadius: 4, overflow: "hidden", minWidth: 20 }}>
                  {!isZero && (
                    <Box
                      sx={{
                        position: "absolute",
                        left: 0, top: 0, bottom: 0,
                        width: `${barPct}%`,
                        background: `linear-gradient(90deg, ${barColor}cc, ${barColor}99)`,
                        borderRadius: 4,
                        transition: "width 0.55s cubic-bezier(0.4,0,0.2,1)",
                      }}
                    />
                  )}
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontFamily: "monospace",
                    fontWeight: 700,
                    color: isZero ? "text.disabled" : barColor,
                    width: 44,
                    textAlign: "right",
                    flexShrink: 0,
                  }}
                >
                  {item.pct_contribution.toFixed(1)}%
                </Typography>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
}

// data360's Risk Limits rolls these raw strategy types into one labelled row;
// a tile owns that row when any of its raw types belongs to the tile.
const EXPOSURE_ROLLUPS = {
  EDFI: ["YIELD TO MATURITY", "EXPECTED CALL", "REDEMPTION/MATURITY/CALL/PUT"],
};

// LMV / SMV / NMV / GMV readout for a tab tile. `exposure` is
// { lmv, smv, nmv, gmv } in dollars, or null while loading / unavailable.
function ExposureStats({ exposure, isActive, asBps, navNumber }) {
  const items = [
    ["LMV", exposure?.lmv],
    // The backend reports SMV as a positive magnitude; show it as the short (negative) exposure it is.
    ["SMV", exposure?.smv == null ? null : -Math.abs(exposure.smv)],
    ["NMV", exposure?.nmv],
    ["GMV", exposure?.gmv],
  ];
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        columnGap: 1.5,
        rowGap: 0.4,
        pl: 1.5,
        borderLeft: "1px solid",
        borderColor: isActive ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.1)",
      }}
    >
      {items.map(([label, value]) => (
        <Tooltip key={label} title={value == null ? "" : formatFullNumber(value)} arrow>
          <Box>
            <Typography sx={{ fontSize: "0.55rem", fontWeight: 700, letterSpacing: "0.05em", lineHeight: 1.1, color: isActive ? "rgba(255,255,255,0.7)" : "text.disabled" }}>
              {label}
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, fontFamily: "monospace", lineHeight: 1.2, whiteSpace: "nowrap", color: value < 0 ? (isActive ? "#ffb4b4" : "#c53030") : (isActive ? "#fff" : (value == null ? "text.disabled" : "text.primary")) }}>
              {formatCurrency(value, { asBps, navNumber })}
            </Typography>
          </Box>
        </Tooltip>
      ))}
    </Box>
  );
}

function StrategyCard({ group, todayPercentiles, prevPercentiles, onNavigate, onClickP1, onClickP5, asBps, navNumber }) {
  const todayP1 = todayPercentiles?.p1?.pnl;
  const todayP5 = todayPercentiles?.p5?.pnl;
  const prevP1 = prevPercentiles?.p1?.pnl;
  const prevP5 = prevPercentiles?.p5?.pnl;

  const drillBtnSx = (accent) => ({
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.4,
    py: 0.55,
    cursor: "pointer",
    borderRadius: 1,
    transition: "all 0.12s ease",
    bgcolor: `${accent}0a`,
    "&:hover": {
      bgcolor: `${accent}18`,
      transform: "scale(1.02)",
    },
  });

  return (
    <Box
      onClick={onNavigate}
      sx={{
        bgcolor: "background.paper",
        borderRadius: 2,
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        boxShadow: "0 1px 3px rgba(0,0,0,0.07), 0 4px 12px rgba(0,0,0,0.05)",
        cursor: onNavigate ? "pointer" : "default",
        "&:hover": onNavigate ? {
          transform: "translateY(-2px)",
          boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
        } : {},
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0, left: 0, right: 0,
          height: "3px",
          background: "#006842",
          borderRadius: "2px 2px 0 0",
        },
      }}
    >
      {/* Strategy name */}
      <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
        <Typography
          sx={{
            fontSize: "0.72rem",
            fontWeight: 700,
            color: "#006842",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {group.label}
        </Typography>
      </Box>

      {/* Data grid — passive, no clicks */}
      <Box sx={{ px: 1.5, pb: 0.75, display: "flex", flexDirection: "column", gap: 0.4, flex: 1 }}>
        <Box sx={{ display: "flex", pt: 0.5 }}>
          <Box sx={{ width: 40 }} />
          <Box sx={{ flex: 1, textAlign: "center" }}>
            <Typography sx={{ fontSize: "0.62rem", fontWeight: 700, color: "#BE9B53", textTransform: "uppercase", letterSpacing: "0.04em" }}>
              1%
            </Typography>
          </Box>
          <Box sx={{ flex: 1, textAlign: "center" }}>
            <Typography sx={{ fontSize: "0.62rem", fontWeight: 700, color: "#6d9aa6", textTransform: "uppercase", letterSpacing: "0.04em" }}>
              5%
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box sx={{ width: 40 }}>
            <Typography sx={{ fontSize: "0.65rem", fontWeight: 600, color: "#006842" }}>Today</Typography>
          </Box>
          <Box sx={{ flex: 1, textAlign: "center" }}>
            <Tooltip title={formatFullNumber(todayP1)} arrow>
              <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "text.primary", fontFamily: "monospace", cursor: "help" }}>
                {formatCurrency(todayP1, { asBps, navNumber })}
              </Typography>
            </Tooltip>
          </Box>
          <Box sx={{ flex: 1, textAlign: "center" }}>
            <Tooltip title={formatFullNumber(todayP5)} arrow>
              <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "text.primary", fontFamily: "monospace", cursor: "help" }}>
                {formatCurrency(todayP5, { asBps, navNumber })}
              </Typography>
            </Tooltip>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box sx={{ width: 40 }}>
            <Typography sx={{ fontSize: "0.65rem", fontWeight: 500, color: "text.disabled" }}>Prev</Typography>
          </Box>
          <Box sx={{ flex: 1, textAlign: "center" }}>
            <Typography sx={{ fontSize: "0.72rem", color: "text.disabled", fontFamily: "monospace" }}>
              {formatCurrency(prevP1, { asBps, navNumber })}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, textAlign: "center" }}>
            <Typography sx={{ fontSize: "0.72rem", color: "text.disabled", fontFamily: "monospace" }}>
              {formatCurrency(prevP5, { asBps, navNumber })}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box sx={{ width: 40 }}>
            <Typography sx={{ fontSize: "0.65rem", fontWeight: 500, color: "text.disabled", fontStyle: "italic" }}>Chg</Typography>
          </Box>
          <Box sx={{ flex: 1, textAlign: "center" }}>
            <ChangeIndicator today={todayP1} prev={prevP1} asBps={asBps} navNumber={navNumber} />
          </Box>
          <Box sx={{ flex: 1, textAlign: "center" }}>
            <ChangeIndicator today={todayP5} prev={prevP5} asBps={asBps} navNumber={navNumber} />
          </Box>
        </Box>
      </Box>

      {/* Action buttons — always visible, clearly interactive */}
      <Box sx={{ display: "flex", gap: 0.5, px: 1.25, pb: 1.25 }}>
        <Box sx={drillBtnSx("#BE9B53")} onClick={(e) => { e.stopPropagation(); onClickP1(); }}>
          <Typography sx={{ fontSize: "0.62rem", fontWeight: 700, color: "#BE9B53", textTransform: "uppercase", letterSpacing: "0.03em" }}>
            Drill 1%
          </Typography>
        </Box>
        <Box sx={drillBtnSx("#6d9aa6")} onClick={(e) => { e.stopPropagation(); onClickP5(); }}>
          <Typography sx={{ fontSize: "0.62rem", fontWeight: 700, color: "#6d9aa6", textTransform: "uppercase", letterSpacing: "0.03em" }}>
            Drill 5%
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default function RiskMonitor({ view = "full" }) {
  const customShocksOnly = view === "custom-shocks";
  const { claims } = useAuth();
  // REACT_APP_FORCE_UPLOAD_UI=true (local .env only) shows the Upload button
  // without the real role.risk_upload claim, for UI testing when your Entra
  // role assignment isn't set up locally. Guarded to dev builds only — the
  // backend still independently enforces require_upload_role on every
  // upload call, so this can never grant a real upload without the real role.
  const canUpload =
    claims?.roles?.includes("role.risk_upload") ||
    (process.env.NODE_ENV === "development" && process.env.REACT_APP_FORCE_UPLOAD_UI === "true");
  const [showBpsOfNav, setShowBpsOfNav] = useState(false);
  const [cacheRefreshing, setCacheRefreshing] = useState(false);
  const [cacheRefreshMsg, setCacheRefreshMsg] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [portfolio, setPortfolio] = useState("");
  // Historical firmwide NAV for the selected date — separate from any live
  // NAV shown elsewhere in the app, since risk analysis should use the NAV
  // that was actually in effect on the date being viewed.
  const [navForDate, setNavForDate] = useState(null);
  const [navForDateError, setNavForDateError] = useState(false);
  // data360 Risk Limits exposure rows ({strategy_type_name, lmv, smv, net_exposure, gross_exposure}) for the selected date.
  const [exposureRows, setExposureRows] = useState([]);
  const urlTab = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(urlTab || "firmwide");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Firmwide VAR (percentiles + prev)
  const [firmVar, setFirmVar] = useState(null);
  // Per-strategy VAR data from bulk endpoint: { "MERGER ARBITRAGE": { today, prev }, ... }
  const [strategyVarData, setStrategyVarData] = useState({});

  // Display groups: the known/labeled STRATEGY_DISPLAY_ORDER entries, plus a
  // fallback card/tab for any strategy_type_name the backend returns that
  // isn't part of a known group — otherwise those types silently never get
  // a card, a tab, or a drill-down.
  const displayGroups = useMemo(() => {
    const covered = new Set(STRATEGY_DISPLAY_ORDER.flatMap((g) => g.strategyTypes));
    const extra = Object.keys(strategyVarData)
      .filter((stName) => !covered.has(stName))
      .map((stName) => ({
        key: `other:${stName}`,
        label: titleCaseStrategyName(stName),
        strategyTypes: [stName],
      }));
    return [...STRATEGY_DISPLAY_ORDER, ...extra];
  }, [strategyVarData]);

  // Live exposure for the tab tiles, from data360's Risk Limits utilization
  // endpoint. Best-effort: a failure leaves the tile values as "—" and never
  // blocks the VaR view.
  useEffect(() => {
    if (!selectedDate) return undefined;
    const controller = new AbortController();
    fetchRiskLimitsExposure(selectedDate, { signal: controller.signal })
      .then((rows) => setExposureRows(rows))
      .catch(() => { if (!controller.signal.aborted) setExposureRows([]); });
    return () => controller.abort();
  }, [selectedDate]);

  // Sums the rows a tile owns (types = its raw strategy types; null = firmwide, i.e. every row).
  const sumExposure = useCallback((types) => {
    const rows = exposureRows.filter((r) => {
      if (!types) return true;
      const raw = EXPOSURE_ROLLUPS[r.strategy_type_name] || [r.strategy_type_name];
      return raw.some((t) => types.includes(t));
    });
    if (rows.length === 0) return null;
    const lmv = rows.reduce((a, r) => a + (Number(r.lmv) || 0), 0);
    const smv = rows.reduce((a, r) => a + (Number(r.smv) || 0), 0);
    return { lmv, smv, nmv: lmv - smv, gmv: lmv + smv };
  }, [exposureRows]);

  const validTabKeys = useMemo(
    () => ["firmwide", ...displayGroups.map((g) => g.key)],
    [displayGroups],
  );
  // Strategy-tab specific data (reloaded per active strategy tab)
  const [stratTabVar, setStratTabVar] = useState(null);
  const [historicalData, setHistoricalData] = useState(null);
  const [pnlDistribution, setPnlDistribution] = useState(null);
  // Previous day's strategy-tab PnL distribution — only source we have for
  // "previous ES" on a strategy tab, since risk-summary (precomputed ES) is
  // firmwide-only. Unused on the firmwide tab (riskSummary.prev covers that).
  const [prevPnlDistribution, setPrevPnlDistribution] = useState(null);
  const [stressScenarios, setStressScenarios] = useState(null);
  const [riskSummary, setRiskSummary] = useState(null);
  const [factorContributions, setFactorContributions] = useState(null);
  const [takeoutRisk, setTakeoutRisk] = useState(null);
  const [takeoutLoading, setTakeoutLoading] = useState(false);
  const [varClickLoading, setVarClickLoading] = useState(null);
  const firmwideCacheRef = useRef(new Map());
  const strategyCacheRef = useRef(new Map());
  const strategyBarCacheRef = useRef(new Map());
  const abortControllerRef = useRef(null);

  // Drill-down modal
  const [drillDown, setDrillDown] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  const setTabFromUi = useCallback((tabKey) => {
    const nextTab = validTabKeys.includes(tabKey) ? tabKey : "firmwide";
    setActiveTab(nextTab);
    const nextParams = new URLSearchParams(searchParams);
    if (nextTab === "firmwide") {
      nextParams.delete("tab");
    } else {
      nextParams.set("tab", nextTab);
    }
    setSearchParams(nextParams);
  }, [searchParams, setSearchParams, validTabKeys]);

  // Keep state in sync with URL (supports deep links + browser back/forward)
  useEffect(() => {
    if (customShocksOnly) {
      if (activeTab !== "firmwide") setActiveTab("firmwide");
      return;
    }
    const nextTab = validTabKeys.includes(urlTab) ? urlTab : "firmwide";
    if (nextTab !== activeTab) {
      setActiveTab(nextTab);
    }
  }, [urlTab, activeTab, validTabKeys, customShocksOnly]);

  // Resolve the active strategy group object (null when on firmwide tab)
  const activeStrategyGroup = useMemo(
    () => activeTab === "firmwide" ? null : displayGroups.find((g) => g.key === activeTab) || null,
    [activeTab, displayGroups],
  );

  // Primary strategy_type_name to use for API calls on strategy tabs
  // (uses first strategyType in the group; for multi-type groups like EDFI, merged below)
  const activeStrategyType = activeStrategyGroup?.strategyTypes[0] || null;

  // Load available dates on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const dates = await fetchAvailableDates();
        if (cancelled) return;
        const sorted = [...dates].sort().reverse();
        setAvailableDates(sorted);
        if (sorted.length > 0) {
          setSelectedDate(sorted[0]);
        } else {
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError("Failed to load available dates");
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Historical NAV for the selected date — refetched every time the date changes.
  useEffect(() => {
    if (!selectedDate) {
      setNavForDate(null);
      setNavForDateError(false);
      return;
    }
    let cancelled = false;
    fetchNavByDate(selectedDate)
      .then((res) => {
        if (cancelled) return;
        setNavForDate(res);
        setNavForDateError(false);
      })
      .catch(() => {
        if (cancelled) return;
        setNavForDate(null);
        setNavForDateError(true);
      });
    return () => { cancelled = true; };
  }, [selectedDate]);

  // Parse a strategy-dashboard API response into the shapes each setter expects
  const _parseDashboard = useCallback((d, date) => ({
    stratTabVar: {
      today: d.var && Object.keys(d.var).length ? d.var : null,
      prev: d.var_prev && Object.keys(d.var_prev).length ? d.var_prev : null,
    },
    historicalData: { data: d.historical_var ?? [], query_time_ms: 0 },
    pnlDistribution: { data: d.pnl_distribution ?? [], total_sims: d.total_sims ?? 0, valuation_date: date },
    prevPnlDistribution: { data: d.pnl_distribution_prev ?? [] },
    stressScenarios: d.stress_scenarios ?? null,
    factorContributions: d.factor_contributions ?? null,
  }), []);

  const loadData = useCallback(async () => {
    if (!selectedDate) return;
    setError(null);

    abortControllerRef.current?.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const { signal } = abortController;
    const opts = { signal };

    try {
      const portfolioParam = portfolio || null;
      const scopeKey = `${selectedDate}|${portfolioParam || "__all__"}`;
      const isFirmwide = activeTab === "firmwide";
      const stratType = isFirmwide ? null : activeStrategyType;
      const strategyKey = `${scopeKey}|${stratType || "__all__"}`;

      // Serve cached strategy bar immediately (tab pills)
      const cachedStrategyBar = strategyBarCacheRef.current.get(scopeKey);
      if (cachedStrategyBar) setStrategyVarData(cachedStrategyBar);

      if (isFirmwide) {
        const cachedFirmwide = firmwideCacheRef.current.get(scopeKey);
        if (cachedFirmwide) {
          setFirmVar(cachedFirmwide.firmVar);
          setHistoricalData(cachedFirmwide.historicalData);
          setStrategyVarData(cachedFirmwide.strategyVarData);
          setPnlDistribution(cachedFirmwide.pnlDistribution);
          setStressScenarios(cachedFirmwide.stressScenarios);
          setRiskSummary(cachedFirmwide.riskSummary);
          setFactorContributions(cachedFirmwide.factorContributions);
          setStratTabVar(null);
          setPrevPnlDistribution(null);
          setLoading(false);
          return;
        }

        // No cache: show spinner only when there is nothing rendered yet
        setLoading(true);

        // Single consolidated request instead of 6 separate ones — they only
        // compete with each other (not sibling requests like strategy-tab
        // prefetch) for the backend's shared blocking-work capacity, and
        // share one 60s timeout budget instead of six independent ones.
        let combined = null;
        try {
          combined = await fetchFirmwideDashboard(selectedDate, portfolioParam, true, opts);
        } catch (err) {
          if (signal.aborted || err?.name === "AbortError" || err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
            return;
          }
          combined = null;
        }
        if (signal.aborted) return;

        // Set individual widget state from the combined response
        setFirmVar(combined?.var_percentiles ?? null);
        setHistoricalData(combined?.historical_var ?? null);
        setStrategyVarData(combined?.var_by_strategy?.data || {});
        setPnlDistribution(combined?.pnl_distribution ?? null);
        setStressScenarios(combined?.stress_scenarios ?? null);
        setRiskSummary(combined?.risk_summary ?? null);
        setFactorContributions(combined?.factor_contributions ?? null);
        setStratTabVar(null);
        setPrevPnlDistribution(null);

        const stratBarData = combined?.var_by_strategy?.data || {};
        if (combined?.var_by_strategy) {
          strategyBarCacheRef.current.set(scopeKey, stratBarData);
        }

        // Only cache/prefetch when every widget genuinely has data — treating
        // a partial (some-null) response the same as a full failure avoids
        // caching nulls that would otherwise poison the tab session.
        const corePieces = [
          combined?.var_percentiles?.today,
          combined?.historical_var,
          combined?.var_by_strategy,
          combined?.pnl_distribution,
          combined?.stress_scenarios,
        ];
        const allCoreSucceeded = combined != null && corePieces.every((p) => p != null);

        if (allCoreSucceeded && combined?.risk_summary != null && combined?.factor_contributions != null) {
          firmwideCacheRef.current.set(scopeKey, {
            firmVar: combined.var_percentiles,
            historicalData: combined.historical_var,
            strategyVarData: stratBarData,
            pnlDistribution: combined.pnl_distribution,
            stressScenarios: combined.stress_scenarios,
            riskSummary: combined.risk_summary,
            factorContributions: combined.factor_contributions,
          });
        }

        if (!allCoreSucceeded) setError("Failed to load risk data");
      } else {
        const cachedStrategy = strategyCacheRef.current.get(strategyKey);
        if (cachedStrategy) {
          setStratTabVar(cachedStrategy.stratTabVar);
          setHistoricalData(cachedStrategy.historicalData);
          setPnlDistribution(cachedStrategy.pnlDistribution);
          setPrevPnlDistribution(cachedStrategy.prevPnlDistribution);
          setStressScenarios(cachedStrategy.stressScenarios);
          setFactorContributions(cachedStrategy.factorContributions);
          setLoading(false);
          return;
        }

        // No cache: show spinner only when nothing is currently displayed
        setLoading(true);

        // Strategy tab: 1 combined request instead of 5+1
        const [dashRes, stratBarRes] = await Promise.allSettled([
          fetchStrategyDashboard(selectedDate, stratType, portfolioParam, opts),
          cachedStrategyBar ? Promise.resolve(null) : fetchVarByStrategy(selectedDate, portfolioParam, true, opts),
        ]);
        if (signal.aborted) return;

        if (dashRes.status === "fulfilled" && dashRes.value) {
          const parsed = _parseDashboard(dashRes.value, selectedDate);
          setStratTabVar(parsed.stratTabVar);
          setHistoricalData(parsed.historicalData);
          setPnlDistribution(parsed.pnlDistribution);
          setPrevPnlDistribution(parsed.prevPnlDistribution);
          setStressScenarios(parsed.stressScenarios);
          setFactorContributions(parsed.factorContributions);
          strategyCacheRef.current.set(strategyKey, parsed);
        } else {
          setError("Failed to load strategy data");
        }

        if (!cachedStrategyBar && stratBarRes.status === "fulfilled" && stratBarRes.value) {
          const strategyBarData = stratBarRes.value.data || {};
          setStrategyVarData(strategyBarData);
          strategyBarCacheRef.current.set(scopeKey, strategyBarData);
        }
      }
    } catch (err) {
      if (signal.aborted || err?.name === "AbortError" || err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
        return;
      }
      setError(err.message || "Failed to load risk data");
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [selectedDate, portfolio, activeTab, activeStrategyType, _parseDashboard]);

  useEffect(() => {
    loadData();
    return () => {
      abortControllerRef.current?.abort();
    };
  }, [loadData]);

  const loadTakeoutRisk = useCallback(() => {
    if (activeTab !== "convertible_arb" || !selectedDate) {
      setTakeoutRisk(null);
      setTakeoutLoading(false);
      return () => {};
    }
    const abortController = new AbortController();
    setTakeoutLoading(true);
    fetchConvertTakeoutRisk(selectedDate, { signal: abortController.signal })
      .then((result) => {
        if (!abortController.signal.aborted) {
          setTakeoutRisk(result);
        }
      })
      .catch((err) => {
        if (abortController.signal.aborted || err?.name === "AbortError" || err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
          return;
        }
        setTakeoutRisk({ data: [] });
      })
      .finally(() => {
        if (!abortController.signal.aborted) setTakeoutLoading(false);
      });
    return () => abortController.abort();
  }, [activeTab, selectedDate]);

  useEffect(() => loadTakeoutRisk(), [loadTakeoutRisk]);

  // Firmwide percentiles (used on firmwide tab)
  const todayPercentiles = firmVar?.today?.percentiles || {};
  const prevPercentiles = firmVar?.prev?.percentiles || {};

  // Strategy-tab percentiles
  const stratTodayPercentiles = stratTabVar?.today?.percentiles || {};
  const stratPrevPercentiles = stratTabVar?.prev?.percentiles || {};

  // Aggregate raw strategy_type_name data into display groups (for firmwide tab strategy cards)
  const strategyVars = useMemo(() => {
    const result = {};
    displayGroups.forEach((group) => {
      const merged = { today: null, prev: null };
      group.strategyTypes.forEach((stName) => {
        const raw = strategyVarData[stName];
        if (!raw) return;
        if (raw.today?.percentiles) {
          if (!merged.today) {
            merged.today = JSON.parse(JSON.stringify(raw.today));
          } else {
            Object.keys(raw.today.percentiles).forEach((pKey) => {
              if (merged.today.percentiles[pKey]) {
                merged.today.percentiles[pKey].pnl += raw.today.percentiles[pKey].pnl;
              } else {
                merged.today.percentiles[pKey] = { ...raw.today.percentiles[pKey] };
              }
            });
          }
        }
        if (raw.prev?.percentiles) {
          if (!merged.prev) {
            merged.prev = JSON.parse(JSON.stringify(raw.prev));
          } else {
            Object.keys(raw.prev.percentiles).forEach((pKey) => {
              if (merged.prev.percentiles[pKey]) {
                merged.prev.percentiles[pKey].pnl += raw.prev.percentiles[pKey].pnl;
              } else {
                merged.prev.percentiles[pKey] = { ...raw.prev.percentiles[pKey] };
              }
            });
          }
        }
      });
      result[group.key] = merged;
    });
    return result;
  }, [strategyVarData, displayGroups]);

  const handleVarClick = async (percentileKey) => {
    const pcts = activeTab === "firmwide" ? todayPercentiles : stratTodayPercentiles;
    const prevVar = activeTab === "firmwide" ? firmVar?.prev : stratTabVar?.prev;
    const sim = pcts[percentileKey];
    if (sim?.sim_number) {
      // Compare against the SAME sim_number on the prior date, not that
      // day's own percentile pick — the two dates only line up apples-to-
      // apples when it's the same underlying simulation on both sides.
      setDrillDown({
        date: selectedDate,
        simNumber: sim.sim_number,
        pnl: sim.pnl,
        percentile: percentileKey,
        portfolio: portfolio || null,
        strategyLabel: activeStrategyGroup?.label,
        compareDate: prevVar?.valuation_date ?? null,
        compareSimNumber: prevVar?.valuation_date != null ? sim.sim_number : null,
      });
      return;
    }
    setVarClickLoading(percentileKey);
    try {
      const portfolioParam = portfolio || null;
      const result = await fetchVarPercentiles(selectedDate, portfolioParam, false, activeStrategyType);
      const fetchedPcts = result?.today?.percentiles || result?.percentiles || {};
      const lazySim = fetchedPcts[percentileKey];
      if (lazySim) {
        setDrillDown({
          date: selectedDate,
          simNumber: lazySim.sim_number,
          pnl: lazySim.pnl,
          percentile: percentileKey,
          portfolio: portfolioParam,
          strategyLabel: activeStrategyGroup?.label,
        });
      }
    } finally {
      setVarClickLoading(null);
    }
  };

  const handleStrategyClick = (group, percentileKey) => {
    const strategyData = strategyVars[group.key];
    const sim = strategyData?.today?.percentiles?.[percentileKey];
    if (sim) {
      // Same sim_number on the prior date — see handleVarClick.
      const compareDate = strategyData?.prev?.valuation_date ?? null;
      setDrillDown({
        date: selectedDate,
        simNumber: sim.sim_number,
        pnl: sim.pnl,
        percentile: percentileKey,
        portfolio: portfolio || null,
        strategyLabel: group.label,
        compareDate,
        compareSimNumber: compareDate != null ? sim.sim_number : null,
      });
    }
  };

  const handleChartDrillDown = (date, simNumber, pnl, percentile) => {
    setDrillDown({
      date,
      simNumber,
      pnl,
      percentile,
      portfolio: portfolio || null,
    });
  };

  const isFirmwide = activeTab === "firmwide";

  // NAV as of the selected date — bps-of-NAV figures must use the NAV that was
  // actually in effect on that date, not the live/current fund NAV.
  const navNumber = navForDate?.nav ?? null;

  // Formats a headline VaR figure as either a standalone $M number or bps of NAV
  const formatHeadline = (value) => {
    if (value == null) return "—";
    if (showBpsOfNav && navNumber) return `${((value / navNumber) * 10000).toFixed(1)} bps`;
    return `${withCommas(value / 1e6)}M`;
  };

  // Current percentiles to pass to charts
  const currentPercentiles = isFirmwide ? todayPercentiles : stratTodayPercentiles;

  // Compute ES from the sorted pnlDistribution (already ascending = worst first)
  const computeEs = (distribution, pct) => {
    if (!distribution || distribution.length === 0) return null;
    const cutoff = Math.max(1, Math.ceil(distribution.length * pct / 100));
    const tail = distribution.slice(0, cutoff);
    return tail.reduce((sum, d) => sum + d.pnl, 0) / tail.length;
  };

  const stratEs5 = useMemo(
    () => isFirmwide ? null : computeEs(pnlDistribution?.data, 5),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isFirmwide, pnlDistribution],
  );
  const stratEs1 = useMemo(
    () => isFirmwide ? null : computeEs(pnlDistribution?.data, 1),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isFirmwide, pnlDistribution],
  );

  // Previous-day ES for a strategy tab — same tail-average approach as
  // stratEs5/stratEs1, applied to the previous day's distribution instead.
  // (Firmwide's "previous ES" instead comes from riskSummary.prev, which is
  // precomputed server-side.)
  const stratEs5Prev = useMemo(
    () => isFirmwide ? null : computeEs(prevPnlDistribution?.data, 5),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isFirmwide, prevPnlDistribution],
  );
  const stratEs1Prev = useMemo(
    () => isFirmwide ? null : computeEs(prevPnlDistribution?.data, 1),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isFirmwide, prevPnlDistribution],
  );

  // Drops every backend cache (in-process + disk) and this component's own
  // frontend-side caches, then re-fetches — for when a replacement file has
  // been uploaded over an already-cached (dataset, date) partition and
  // wouldn't otherwise show up until a hard reload restarted the backend.
  const handleRefreshCache = async () => {
    setCacheRefreshing(true);
    setCacheRefreshMsg(null);
    try {
      await refreshCache();
      firmwideCacheRef.current.clear();
      strategyCacheRef.current.clear();
      strategyBarCacheRef.current.clear();
      loadData();
      loadTakeoutRisk();
      setCacheRefreshMsg("Refreshed");
    } catch (err) {
      setCacheRefreshMsg("Refresh failed");
    } finally {
      setCacheRefreshing(false);
      setTimeout(() => setCacheRefreshMsg(null), 2500);
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 1.5, md: 2.5 }, width: "100%", bgcolor: "#f8f9fa", overflowX: "hidden" }}>
      {/* Header + Filters */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: { xs: 1, sm: 2 },
          mb: { xs: 1, sm: 1.5 },
        }}
      >
        <Box sx={{ display: "flex", gap: { xs: 1, sm: 2 }, flexWrap: "wrap", alignItems: "center" }}>
          {canUpload && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => setUploadOpen(true)}
            sx={{
              color: "#006842",
              borderColor: "#006842",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { borderColor: "#005233", bgcolor: "rgba(0,104,66,0.06)" },
            }}
          >
            Upload
          </Button>
          )}
          <FormControl size="small" sx={{ minWidth: { xs: 120, sm: 160 } }}>
            <InputLabel>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <CalendarTodayIcon sx={{ fontSize: 16 }} /> Date
              </Box>
            </InputLabel>
            <Select
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              label="Date"
            >
              {availableDates.map((d) => (
                <MenuItem key={d} value={d}>{d}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: { xs: 100, sm: 140 } }}>
            <InputLabel>Portfolio</InputLabel>
            <Select
              value={portfolio}
              onChange={(e) => setPortfolio(e.target.value)}
              label="Portfolio"
            >
              <MenuItem value="">All (Firmwide)</MenuItem>
              <MenuItem value="GCEMF">GCEMF</MenuItem>
              <MenuItem value="GCM">GCM</MenuItem>
              <MenuItem value="GIP">GIP</MenuItem>
            </Select>
          </FormControl>

          {selectedDate && (
            <Tooltip
              title={
                navForDateError
                  ? "Failed to load NAV for this date."
                  : navForDate && navForDate.as_of !== selectedDate
                    ? `No NAV was recorded on ${selectedDate} — showing the last known value, recorded ${navForDate.as_of}.`
                    : "Historical NAV as recorded on this date — not the live/current fund NAV."
              }
              arrow
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 1.5,
                  bgcolor: "background.paper",
                  border: "1px solid rgba(0,104,66,0.2)",
                  cursor: "help",
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: "0.62rem", fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    NAV Update: Firmwide
                  </Typography>
                  <Typography sx={{ fontSize: "0.9rem", fontWeight: 800, fontFamily: "monospace", color: "#006842", lineHeight: 1.15 }}>
                    {navForDateError ? "—" : navForDate?.nav != null ? formatCurrency(navForDate.nav) : "…"}
                  </Typography>
                  <Typography sx={{ fontSize: "0.65rem", color: "text.secondary", fontWeight: 600 }}>
                    {navForDateError
                      ? `No NAV for ${selectedDate}`
                      : navForDate && navForDate.as_of !== selectedDate
                        ? `as of ${navForDate.as_of} (carried fwd)`
                        : `as of ${selectedDate}`}
                  </Typography>
                </Box>
                <HelpOutlineIcon sx={{ fontSize: 14, color: "text.disabled" }} />
              </Box>
            </Tooltip>
          )}
        </Box>

        <Box sx={{ flex: 1 }} />

        {canUpload && (
          <Tooltip title={cacheRefreshMsg || "Pull the latest files from storage right away, instead of waiting on the server's cache"} arrow>
            <span>
              <Button
                size="small"
                variant="outlined"
                disabled={cacheRefreshing}
                startIcon={cacheRefreshing ? <CircularProgress size={14} sx={{ color: "#006842" }} /> : <RefreshIcon />}
                onClick={handleRefreshCache}
                sx={{
                  color: "#006842",
                  borderColor: "#006842",
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": { borderColor: "#005233", bgcolor: "rgba(0,104,66,0.06)" },
                }}
              >
                {cacheRefreshMsg || "Refresh Cache"}
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>

      {/* Tab Bar */}
      {!customShocksOnly && (
      <Box sx={{ display: "flex", alignItems: "stretch", gap: 1, mb: { xs: 1.5, sm: 2 } }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flex: "0 0 96px" }}>
          <Tooltip title="Monte Carlo VaR analysis across firm and strategies" arrow>
            <Box sx={{ display: "flex" }}>
              <GVarLogo height={68} />
            </Box>
          </Tooltip>
        </Box>

        {/* Tiles share the remaining width and wrap onto new rows once they hit their minimum width */}
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", alignItems: "stretch", gap: 1, flex: 1, minWidth: 0 }}>

        {/* Firmwide tab */}
        {(() => {
          const isActive = activeTab === "firmwide";
          const fwVaR5 = riskSummary?.firmwide?.var_95 ?? todayPercentiles.p5?.pnl;
          const fwVaR5Prev = riskSummary?.prev?.firmwide?.var_95 ?? prevPercentiles.p5?.pnl;
          const delta = fwVaR5 != null && fwVaR5Prev != null ? fwVaR5 - fwVaR5Prev : null;
          const improved = delta != null && delta > 0;
          return (
            <Box
              onClick={() => setTabFromUi("firmwide")}
              sx={{
                cursor: "pointer",
                borderRadius: 2,
                minWidth: 0,
                px: 1.5,
                py: 1.25,
                border: "1.5px solid",
                borderColor: isActive ? "#006842" : "rgba(0,104,66,0.2)",
                bgcolor: isActive ? "#006842" : "background.paper",
                boxShadow: isActive
                  ? "0 2px 12px rgba(0,104,66,0.25)"
                  : "0 1px 3px rgba(0,0,0,0.06)",
                transition: "all 0.15s ease",
                "&:hover": !isActive ? { borderColor: "#006842", boxShadow: "0 2px 8px rgba(0,104,66,0.15)" } : {},
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.4 }}>
                <DomainIcon sx={{ fontSize: 11, color: isActive ? "rgba(255,255,255,0.8)" : "#006842" }} />
                <Typography sx={{ fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: isActive ? "rgba(255,255,255,0.85)" : "#006842" }}>
                  Firmwide
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box sx={{ flex: "0 0 auto", minWidth: 84 }}>
                  <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, fontFamily: "monospace", color: isActive ? "#fff" : "text.primary", lineHeight: 1.1 }}>
                    {formatHeadline(fwVaR5)}
                  </Typography>
                  {delta != null && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.3, mt: 0.3 }}>
                      {improved
                        ? <TrendingUpIcon sx={{ fontSize: 10, color: isActive ? "rgba(255,255,255,0.75)" : "#006842" }} />
                        : <TrendingDownIcon sx={{ fontSize: 10, color: isActive ? "rgba(255,200,200,0.9)" : "#c53030" }} />}
                      <Typography sx={{ fontSize: "0.6rem", fontWeight: 700, color: isActive ? (improved ? "rgba(255,255,255,0.75)" : "rgba(255,200,200,0.9)") : (improved ? "#006842" : "#c53030") }}>
                        {formatDeltaValue(fwVaR5, fwVaR5Prev, { asBps: showBpsOfNav, navNumber }).text}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <ExposureStats exposure={sumExposure(null)} isActive={isActive} asBps={showBpsOfNav} navNumber={navNumber} />
              </Box>
            </Box>
          );
        })()}

        {/* Strategy tabs */}
        {displayGroups.map((group) => {
          const isActive = activeTab === group.key;
          const data = strategyVars[group.key];
          const varVal = data?.today?.percentiles?.p5?.pnl;
          const varPrev = data?.prev?.percentiles?.p5?.pnl;
          const delta = varVal != null && varPrev != null ? varVal - varPrev : null;
          const improved = delta != null && delta > 0;
          const hasData = varVal != null;
          return (
            <Box
              key={group.key}
              onClick={() => setTabFromUi(group.key)}
              sx={{
                cursor: "pointer",
                borderRadius: 2,
                minWidth: 0,
                px: 1.5,
                py: 1.25,
                border: "1.5px solid",
                borderColor: isActive ? "#006842" : "rgba(0,0,0,0.1)",
                bgcolor: isActive ? "#006842" : "background.paper",
                boxShadow: isActive
                  ? "0 2px 12px rgba(0,104,66,0.25)"
                  : "0 1px 3px rgba(0,0,0,0.06)",
                transition: "all 0.15s ease",
                "&:hover": !isActive ? { borderColor: "#006842", bgcolor: "rgba(0,104,66,0.04)", boxShadow: "0 2px 8px rgba(0,104,66,0.12)" } : {},
              }}
            >
              <Typography sx={{ fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: isActive ? "rgba(255,255,255,0.85)" : "text.secondary", mb: 0.4, whiteSpace: "nowrap" }}>
                {group.label}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box sx={{ flex: "0 0 auto", minWidth: 84 }}>
                  <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, fontFamily: "monospace", color: isActive ? "#fff" : (hasData ? "text.primary" : "text.disabled"), lineHeight: 1.1 }}>
                    {hasData ? formatHeadline(varVal) : "—"}
                  </Typography>
                  {delta != null ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.3, mt: 0.3 }}>
                      {improved
                        ? <TrendingUpIcon sx={{ fontSize: 10, color: isActive ? "rgba(255,255,255,0.75)" : "#006842" }} />
                        : <TrendingDownIcon sx={{ fontSize: 10, color: isActive ? "rgba(255,200,200,0.9)" : "#c53030" }} />}
                      <Typography sx={{ fontSize: "0.6rem", fontWeight: 700, color: isActive ? (improved ? "rgba(255,255,255,0.75)" : "rgba(255,200,200,0.9)") : (improved ? "#006842" : "#c53030") }}>
                        {formatDeltaValue(varVal, varPrev, { asBps: showBpsOfNav, navNumber }).text}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ height: 14 }} />
                  )}
                </Box>
                <ExposureStats exposure={sumExposure(group.strategyTypes)} isActive={isActive} asBps={showBpsOfNav} navNumber={navNumber} />
              </Box>
            </Box>
          );
        })}
        </Box>
      </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && <GVarLoader />}
      {!loading && (
        <>
          {!customShocksOnly && (
          <>
          {/* ── VAR / ES Section ─────────────────────────────────── */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mb: 1.25 }}>
              <Typography
                sx={{ fontWeight: 700, fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.1em", color: "#006842" }}
              >
                {isFirmwide ? "Firmwide Value at Risk" : `${activeStrategyGroup?.label} Value at Risk`}
              </Typography>
              <Tooltip title="Value at Risk and Expected Shortfall from Monte Carlo simulations. Click a card for strategy breakdown." arrow>
                <HelpOutlineIcon sx={{ fontSize: 15, color: "#006842", opacity: 0.6, cursor: "help" }} />
              </Tooltip>
              {activeTab === "convertible_arb" && (
                <Button
                  size="small"
                  onClick={() => document.getElementById("convert-takeout-risk")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                  sx={{ fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "#BE9B53", minWidth: 0, py: 0.25, fontWeight: 700 }}
                >
                  Takeout Risk
                </Button>
              )}
              <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showBpsOfNav}
                      onChange={(e) => setShowBpsOfNav(e.target.checked)}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": { color: "#006842" },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#006842" },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                      bps of NAV
                    </Typography>
                  }
                  sx={{ mr: 0 }}
                />
                {(isFirmwide ? (riskSummary?.firmwide?.n_sims || firmVar?.today?.total_sims) : stratTabVar?.today?.total_sims) && (
                  <Typography sx={{ fontSize: "0.7rem", color: "text.disabled" }}>
                    {(isFirmwide ? (riskSummary?.firmwide?.n_sims || firmVar?.today?.total_sims) : stratTabVar?.today?.total_sims).toLocaleString()} sims
                  </Typography>
                )}
              </Box>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", lg: "1fr 1fr 1fr" }, gap: 1.5, alignItems: "stretch" }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <VarBubble
                  label="VAR 5%"
                  esLabel="ES 5%"
                  todayValue={isFirmwide ? (riskSummary?.firmwide?.var_95 ?? todayPercentiles.p5?.pnl) : stratTodayPercentiles.p5?.pnl}
                  prevValue={isFirmwide ? (riskSummary?.prev?.firmwide?.var_95 ?? prevPercentiles.p5?.pnl) : stratPrevPercentiles.p5?.pnl}
                  esValue={isFirmwide ? riskSummary?.firmwide?.es_95 : stratEs5}
                  prevEsValue={isFirmwide ? riskSummary?.prev?.firmwide?.es_95 : stratEs5Prev}
                  accentColor="#6d9aa6"
                  loading={varClickLoading === "p5"}
                  onClick={() => handleVarClick("p5")}
                  asBps={showBpsOfNav}
                  navNumber={navNumber}
                />
                <VarBubble
                  label="VAR 1%"
                  esLabel="ES 1%"
                  todayValue={isFirmwide ? (riskSummary?.firmwide?.var_99 ?? todayPercentiles.p1?.pnl) : stratTodayPercentiles.p1?.pnl}
                  prevValue={isFirmwide ? (riskSummary?.prev?.firmwide?.var_99 ?? prevPercentiles.p1?.pnl) : stratPrevPercentiles.p1?.pnl}
                  esValue={isFirmwide ? riskSummary?.firmwide?.es_99 : stratEs1}
                  prevEsValue={isFirmwide ? riskSummary?.prev?.firmwide?.es_99 : stratEs1Prev}
                  accentColor="#BE9B53"
                  loading={varClickLoading === "p1"}
                  onClick={() => handleVarClick("p1")}
                  asBps={showBpsOfNav}
                  navNumber={navNumber}
                />
              </Box>

              <FactorContributionChart
                title="Top 5 Risk Drivers"
                items={factorContributions?.top || []}
                barColor="#c53030"
                emptyMessage="No factor data"
              />
              <FactorContributionChart
                title="Top 5 Risk Diversifiers"
                items={factorContributions?.bottom || []}
                barColor="#006842"
                emptyMessage="No factor data"
              />
            </Box>
          </Box>

          {/* ── Strategy Breakdown cards (firmwide tab only) ──── */}
          {isFirmwide && (
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 700, fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.1em", color: "#006842", mb: 1.25 }}>
                Strategy Breakdown
              </Typography>
              {/* Cards wrap onto new rows instead of scrolling sideways */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
                  gap: { xs: 1, sm: 1.5 },
                }}
              >
                {displayGroups.map((group) => {
                  const data = strategyVars[group.key];
                  return (
                    <Box key={group.key} sx={{ minWidth: 0 }}>
                      <StrategyCard
                        group={group}
                        todayPercentiles={data?.today?.percentiles}
                        prevPercentiles={data?.prev?.percentiles}
                        onNavigate={() => setTabFromUi(group.key)}
                        onClickP1={() => handleStrategyClick(group, "p1")}
                        onClickP5={() => handleStrategyClick(group, "p5")}
                        asBps={showBpsOfNav}
                        navNumber={navNumber}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* ── Charts ───────────────────────────────────────── */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 1.5, mb: 2 }}>
            <Box sx={{ bgcolor: "background.paper", borderRadius: 2, p: { xs: 1.25, sm: 2 }, boxShadow: "0 1px 3px rgba(0,0,0,0.07), 0 4px 12px rgba(0,0,0,0.05)", minWidth: 0, overflow: "hidden" }}>
              <Typography sx={{ fontWeight: 700, fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.09em", color: "#006842", mb: 1.25 }}>
                PnL Distribution
              </Typography>
              <PnlDistributionChart
                distribution={pnlDistribution?.data || []}
                percentiles={currentPercentiles}
                onSimClick={(simNumber, pnl) => {
                  setDrillDown({ date: selectedDate, simNumber, pnl, percentile: "sim", portfolio: portfolio || null });
                }}
                showBpsOfNav={showBpsOfNav}
                navNumber={navNumber}
              />
            </Box>
            <Box sx={{ bgcolor: "background.paper", borderRadius: 2, p: { xs: 1.25, sm: 2 }, boxShadow: "0 1px 3px rgba(0,0,0,0.07), 0 4px 12px rgba(0,0,0,0.05)", minWidth: 0, overflow: "hidden" }}>
              <Typography sx={{ fontWeight: 700, fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.09em", color: "#006842", mb: 1.25 }}>
                Historical VAR
              </Typography>
              <HistoricalVarChart
                data={historicalData?.data || []}
                onBarClick={handleChartDrillDown}
                showBpsOfNav={showBpsOfNav}
                navNumber={navNumber}
              />
            </Box>
          </Box>
          </>
          )}

          {/* ── Stress Scenarios ─────────────────────────────── */}
          <Box sx={{ mb: 2 }}>
            <StressScenarioPanel
              data={stressScenarios}
              loading={false}
              customOnly={customShocksOnly}
              singleStrategy={customShocksOnly || isFirmwide ? null : activeStrategyGroup?.label}
              showBpsOfNav={showBpsOfNav}
              beforeDefinitions={
                activeTab === "convertible_arb" ? (
                  <ConvertTakeoutRiskPanel data={takeoutRisk} loading={takeoutLoading} />
                ) : null
              }
              onSimClick={(simNumber, pnl, scenarioLabel) => {
                setDrillDown({
                  date: selectedDate,
                  simNumber,
                  pnl,
                  percentile: "stress",
                  portfolio: portfolio || null,
                  strategyLabel: scenarioLabel,
                });
              }}
            />
          </Box>
        </>
      )}

      {drillDown && (
        <StrategyBreakdownModal
          open={!!drillDown}
          onClose={() => setDrillDown(null)}
          date={drillDown.date}
          simNumber={drillDown.simNumber}
          pnl={drillDown.pnl}
          percentile={drillDown.percentile}
          portfolio={drillDown.portfolio}
          strategyLabel={drillDown.strategyLabel}
          compareDate={drillDown.compareDate}
          compareSimNumber={drillDown.compareSimNumber}
        />
      )}

      {canUpload && (
        <RiskMonitorUpload open={uploadOpen} onClose={() => setUploadOpen(false)} />
      )}
    </Box>
  );
}
